import { useQueries, useQueryClient } from '@tanstack/react-query';

export function createGlobalStates(queryKey, queryFunction, defaultTime = 60_000) {
  return function (queryArray, condition = true, id = null, options = {}) {
    const queryClient = useQueryClient();
    // `fresh` (JED-1148): read every entry from the server each time the
    // caller mounts, as desktop Create Task reads the JeddAI kill-switch. It
    // uses its own key, so another screen's cached copy (up to defaultTime
    // old) is never shown or acted on meanwhile, and keeps nothing once the
    // caller unmounts. Without it nothing changes.
    const fresh = Boolean(options && options.fresh);

    const queries = queryArray
      ? queryArray.map((current) => ({
          queryKey: fresh
            ? [queryKey, id ? current[id] : current, 'fresh']
            : [queryKey, id ? current[id] : current],
          queryFn: () => queryFunction(id ? current[id] : current),
          refetchOnMount: fresh ? 'always' : false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          enabled: condition,
          staleTime: fresh ? 0 : defaultTime,
          cacheTime: fresh ? 0 : defaultTime,
        }))
      : [];

    const results = useQueries({
      queries,
    });

    const data = results.map((result) => result.data);
    const isLoadingdata =
      condition && results.some((result) => result.isLoading);
    const isFetching =
      condition && results.some((result) => result.isFetching);
    const isError = results.some((result) => result.isError);
    const errors = results.filter((result) => result.error).map((result) => result.error);

    function setData(newData, index) {
      if (index !== undefined && queries[index]) {
        queryClient.setQueryData(queries[index].queryKey, newData);
      }
    }

    function resetData() {
      // One request per entry; see resetData in ./index.js.
      queries.forEach((query) => {
        queryClient.invalidateQueries({
          queryKey: query.queryKey,
          refetchType: 'all',
        });
      });
    }

    // queryKeys: the prefixes resetData refreshes.
    const queryKeys = queries.map((query) => query.queryKey);

    return {
      data,
      isLoadingdata,
      isFetching,
      isError,
      errors,
      setData,
      resetData,
      queryKeys,
    };
  };
}
